import { z } from 'zod';

export const ResellerGroupMemberStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'CHECKED_IN',
  'NO_SHOW',
  'CANCELLED',
]);
export type ResellerGroupMemberStatus = z.infer<typeof ResellerGroupMemberStatusSchema>;

export const ResellerGroupMemberSchema = z.object({
  id: z.string(),
  memberName: z.string(),
  memberEmail: z.string().nullable(),
  memberPhone: z.string().nullable(),
  status: ResellerGroupMemberStatusSchema,
});
export type ResellerGroupMember = z.infer<typeof ResellerGroupMemberSchema>;

export const ResellerGroupTypeSchema = z.enum([
  'FAMILY',
  'SCHOOL',
  'CORPORATE',
  'TOUR',
  'EVENT',
  'OTHER',
]);
export type ResellerGroupType = z.infer<typeof ResellerGroupTypeSchema>;

export const ResellerGroupBookingSchema = z.object({
  bookingNumber: z.string(),
  groupName: z.string(),
  groupType: ResellerGroupTypeSchema,
  visitDate: z.string(),
  arrivalTime: z.string().nullable(),
  expectedMembers: z.number().int(),
  status: z.string(),
  ticketTypeId: z.string(),
  ticketTypeName: z.string(),
  basePricePerPerson: z.number(),
  dynamicMultiplier: z.number(),
  groupDiscountPercent: z.number(),
  effectivePricePerPerson: z.number(),
  totalAmount: z.number(),
  depositAmount: z.number(),
  depositDueDate: z.string().nullable(),
  balanceDueDate: z.string().nullable(),
  groupQrToken: z.string().nullable(),
  coordinatorName: z.string(),
  createdAt: z.string(),
  metadata: z.record(z.unknown()).nullable(),
  members: z.array(ResellerGroupMemberSchema).optional(),
  resellerDiscountPercent: z.number().optional(),
  customerPriceAmount: z.number().optional(),
  ledgerEntryId: z.string().nullable().optional(),
  upstream: z
    .object({
      status: z.string().optional(),
      groupType: z.string().optional(),
      arrivalTime: z.string().nullable().optional(),
      coordinatorName: z.string().nullable().optional(),
      coordinatorEmail: z.string().nullable().optional(),
      coordinatorPhone: z.string().nullable().optional(),
      schoolName: z.string().nullable().optional(),
      companyName: z.string().nullable().optional(),
      expectedMembers: z.number().int().optional(),
      basePricePerPersonIdr: z.number().optional(),
      dynamicMultiplier: z.number().optional(),
      groupDiscountPercent: z.number().optional(),
      effectivePricePerPersonIdr: z.number().optional(),
      subtotalIdr: z.number().optional(),
      taxIdr: z.number().optional(),
      totalIdr: z.number().optional(),
      depositAmountIdr: z.number().optional(),
      depositPaidAt: z.string().nullable().optional(),
      balanceDueDate: z.string().nullable().optional(),
      balancePaidAt: z.string().nullable().optional(),
      paymentMode: z.enum(['FULL_UPFRONT', 'DEPOSIT']).nullable().optional(),
      rejectionReason: z.string().nullable().optional(),
      groupQrToken: z.string().nullable().optional(),
      members: z.array(ResellerGroupMemberSchema).optional(),
      groupOrder: z
        .object({ members: z.array(ResellerGroupMemberSchema).optional() })
        .optional(),
      addonLines: z
        .array(
          z.object({
            id: z.string(),
            ticketTypeName: z.string().optional(),
            attractionName: z.string().optional(),
            quantity: z.number().int(),
            unitPriceIdr: z.number(),
            subtotalIdr: z.number(),
          }),
        )
        .optional(),
    })
    .optional(),
});
export type ResellerGroupBooking = z.infer<typeof ResellerGroupBookingSchema>;

export const ResellerGroupsResponseSchema = z.object({
  data: z.array(ResellerGroupBookingSchema),
  meta: z.object({
    page: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
  }),
});
export type ResellerGroupsResponse = z.infer<typeof ResellerGroupsResponseSchema>;

export const ResellerGroupPricePreviewSchema = z.object({
  basePricePerPersonIdr: z.number(),
  dynamicMultiplier: z.number(),
  groupDiscountPercent: z.number(),
  effectivePricePerPersonIdr: z.number(),
  subtotalIdr: z.number(),
  totalIdr: z.number(),
  depositAmountIdr: z.number(),
  depositPercent: z.number(),
  balanceAmountIdr: z.number(),
  discountIdr: z.number(),
  taxIdr: z.number(),
  couponDiscountIdr: z.number(),
  paymentMode: z.string(),
  tier: z.unknown(),
  requiresApproval: z.boolean(),
  addonSubtotalIdr: z.number().optional(),
});
export type ResellerGroupPricePreview = z.infer<typeof ResellerGroupPricePreviewSchema>;

export const ResellerGroupsFiltersSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  status: z.string().optional(),
  visitDateFrom: z.string().optional(),
  visitDateTo: z.string().optional(),
  search: z.string().optional(),
});
export type ResellerGroupsFilters = z.infer<typeof ResellerGroupsFiltersSchema>;

export const CreateGroupSchema = z.object({
  groupType: ResellerGroupTypeSchema,
  visitDate: z.string(),
  arrivalTime: z.string(),
  expectedMembers: z.number().int().positive(),
  ticketTypeId: z.string(),
  groupName: z.string().optional(),
  schoolName: z.string().optional(),
  companyName: z.string().optional(),
  purchaseOrderNumber: z.string().optional(),
  emergencyContactName: z.string().optional(),
  emergencyContactPhone: z.string().optional(),
  coordinatorName: z.string().optional(),
  coordinatorEmail: z.string().optional(),
  coordinatorPhone: z.string().optional(),
  addonLines: z
    .array(
      z.object({
        attractionTicketTypeId: z.string(),
        quantity: z.number().int().positive(),
        slotId: z.string().optional(),
      }),
    )
    .optional(),
  specialRequirements: z.string().optional(),
  notes: z.string().optional(),
  idempotencyKey: z.string().optional(),
  resellerDiscountPercent: z.number().min(0).max(100).optional(),
});
export type CreateGroupInput = z.infer<typeof CreateGroupSchema>;

export const UpdateGroupMembersSchema = z.object({
  bookingNumber: z.string(),
  members: z.array(
    z.object({
      memberName: z.string().min(1),
      memberEmail: z.string().email().optional(),
      memberPhone: z.string().optional(),
    }),
  ),
});
export type UpdateMembersInput = z.infer<typeof UpdateGroupMembersSchema>;

export const ConfirmGroupSchema = z.object({
  bookingNumber: z.string(),
});
export type ConfirmGroupInput = z.infer<typeof ConfirmGroupSchema>;

export const CancelGroupSchema = z.object({
  bookingNumber: z.string(),
  reason: z.string().min(1),
});
export type CancelGroupInput = z.infer<typeof CancelGroupSchema>;

export const GroupPricePreviewInputSchema = z.object({
  groupType: z.string().optional(),
  expectedMembers: z.number().int().positive().optional(),
  visitDate: z.string().optional(),
  ticketTypeId: z.string().optional(),
  addonLines: z
    .array(
      z.object({
        attractionTicketTypeId: z.string(),
        quantity: z.number().int().positive(),
      }),
    )
    .optional(),
});
export type GroupPricePreviewInput = z.infer<typeof GroupPricePreviewInputSchema>;

export const ResellerTicketTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
});
export type ResellerTicketType = z.infer<typeof ResellerTicketTypeSchema>;

export const AttractionTicketTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
  basePriceIdr: z.number().optional(),
});
export type AttractionTicketType = z.infer<typeof AttractionTicketTypeSchema>;

export const ResellerAttractionAvailabilityStatusSchema = z.enum([
  'AVAILABLE',
  'LIMITED',
  'SOLD_OUT',
  'CLOSED',
]);
export type ResellerAttractionAvailabilityStatus = z.infer<typeof ResellerAttractionAvailabilityStatusSchema>;

export const ResellerAttractionAvailabilityItemSchema = z.object({
  ticketTypeId: z.string(),
  attractionId: z.string().nullable(),
  name: z.string(),
  status: ResellerAttractionAvailabilityStatusSchema,
  availableSlots: z.number().int().optional(),
});
export type ResellerAttractionAvailabilityItem = z.infer<typeof ResellerAttractionAvailabilityItemSchema>;
